import type { Disposable } from "@/shared/contracts/lifecycle";
import {
  DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS,
  UPGRADE_HOVER_REPEATER_MIN_INTERVAL_MS,
  type UpgradeRepeatInitialIntervalSource,
  type UpgradeRepeatSpeedMultiplierSource,
} from "@/features/hoverIdle/upgrades/upgradeRepeatTiming";
import {
  UPGRADE_HOVER_REPEATER_DEFAULT_DECAY,
  advanceUpgradeRepeatCadence,
  createUpgradeRepeatCadenceState,
  resetUpgradeRepeatCadenceState,
  type UpgradeRepeatCadenceConfig,
} from "@/features/hoverIdle/upgrades/upgradeRepeatCadence";

const DEFAULT_INITIAL_INTERVAL_MS = DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS;
const DEFAULT_MIN_INTERVAL_MS = UPGRADE_HOVER_REPEATER_MIN_INTERVAL_MS;
const DEFAULT_DECAY = UPGRADE_HOVER_REPEATER_DEFAULT_DECAY;

/**
 * Real-time lower bound for the timer this repeater schedules. Admin speed is
 * applied to simulated repeat time inside each tick, while this floor keeps
 * browser timer frequency bounded.
 */
const DEFAULT_REAL_TIMER_FLOOR_MS = 16;
const DEFAULT_MAX_REPEATS_PER_TICK = 250;

export type UpgradeRepeatEventBudgetSource = number | (() => number);

export interface UpgradeHoverRepeatContext {
  readonly nextIntervalMs: number;
}

export type UpgradeHoverRepeatResult = boolean | void;

export interface UpgradeHoverRepeaterOptions {
  readonly onFire: (
    context: UpgradeHoverRepeatContext,
  ) => UpgradeHoverRepeatResult;
  readonly onBurstStart?: () => void;
  readonly onBurstEnd?: () => void;
  readonly initialIntervalMs?: UpgradeRepeatInitialIntervalSource;
  readonly minIntervalMs?: number;
  readonly decay?: number;
  readonly speedMultiplier?: UpgradeRepeatSpeedMultiplierSource;
  readonly eventBudgetPerTick?: UpgradeRepeatEventBudgetSource;
}

export interface UpgradeHoverRepeater extends Disposable {
  getInitialIntervalMs(): number;
  setEngaged(isEngaged: boolean): void;
}

export function createUpgradeHoverRepeater(
  options: UpgradeHoverRepeaterOptions,
): UpgradeHoverRepeater {
  const resolveInitialIntervalMs = (): number => {
    if (typeof options.initialIntervalMs === "function") {
      return options.initialIntervalMs();
    }
    return options.initialIntervalMs ?? DEFAULT_INITIAL_INTERVAL_MS;
  };
  const resolveSpeedMultiplier = (): number => {
    const raw =
      typeof options.speedMultiplier === "function"
        ? options.speedMultiplier()
        : options.speedMultiplier;
    if (raw === undefined || !Number.isFinite(raw) || raw <= 0) {
      return 1;
    }
    return raw;
  };
  const resolveEventBudgetPerTick = (): number => {
    const raw =
      typeof options.eventBudgetPerTick === "function"
        ? options.eventBudgetPerTick()
        : options.eventBudgetPerTick;
    if (raw === undefined || !Number.isFinite(raw) || raw <= 0) {
      return DEFAULT_MAX_REPEATS_PER_TICK;
    }
    return Math.max(1, Math.floor(raw));
  };
  // Converts a base interval into the real-time timer/context interval without
  // letting admin speed schedule sub-floor timers.
  const toEffectiveIntervalMs = (baseIntervalMs: number): number =>
    Math.max(
      DEFAULT_REAL_TIMER_FLOOR_MS,
      baseIntervalMs / resolveSpeedMultiplier(),
    );

  let cadenceConfig: UpgradeRepeatCadenceConfig = {
    initialIntervalMs: resolveInitialIntervalMs(),
    minIntervalMs: options.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS,
    decay: options.decay ?? DEFAULT_DECAY,
  };
  const cadenceState = createUpgradeRepeatCadenceState(cadenceConfig);

  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastTickAtMs: number | null = null;
  let isActive = false;
  let burstDepth = 0;

  const resetRepeatProgress = (): void => {
    cadenceConfig = {
      ...cadenceConfig,
      initialIntervalMs: resolveInitialIntervalMs(),
    };
    resetUpgradeRepeatCadenceState(cadenceState, cadenceConfig);
  };

  const resolveNextTimerDelayMs = (): number => {
    const remainingRepeatMs = Math.max(
      0,
      cadenceState.currentBaseIntervalMs - cadenceState.accumulatedRepeatMs,
    );
    return Math.max(
      DEFAULT_REAL_TIMER_FLOOR_MS,
      remainingRepeatMs / resolveSpeedMultiplier(),
    );
  };

  const beginBurst = (): void => {
    if (burstDepth === 0) {
      options.onBurstStart?.();
    }
    burstDepth += 1;
  };

  const endBurst = (): void => {
    if (burstDepth <= 0) {
      return;
    }
    burstDepth -= 1;
    if (burstDepth === 0) {
      options.onBurstEnd?.();
    }
  };

  const processAccumulatedRepeats = (): void => {
    let processedRepeats = 0;

    beginBurst();
    try {
      const eventBudget = resolveEventBudgetPerTick();
      while (isActive && processedRepeats < eventBudget) {
        const fireCount = advanceUpgradeRepeatCadence(
          cadenceState,
          cadenceConfig,
          0,
          { maxFires: 1 },
        );
        if (fireCount === 0) break;

        const result = options.onFire({
          nextIntervalMs: toEffectiveIntervalMs(
            cadenceState.currentBaseIntervalMs,
          ),
        });
        if (!isActive) {
          return;
        }
        if (result === false) {
          resetRepeatProgress();
          cadenceState.hasFiredInitial = true;
          return;
        }

        processedRepeats += 1;
      }
    } finally {
      endBurst();
    }
  };

  const scheduleNext = (): void => {
    timer = setTimeout(() => {
      timer = null;
      if (!isActive) return;

      const nowMs = Date.now();
      const realElapsedMs =
        lastTickAtMs === null ? 0 : Math.max(0, nowMs - lastTickAtMs);
      lastTickAtMs = nowMs;

      cadenceState.accumulatedRepeatMs +=
        realElapsedMs * resolveSpeedMultiplier();
      processAccumulatedRepeats();

      if (isActive) {
        scheduleNext();
      }
    }, resolveNextTimerDelayMs());
  };

  const stop = (): void => {
    isActive = false;
    resetRepeatProgress();
    lastTickAtMs = null;
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return {
    getInitialIntervalMs(): number {
      return resolveInitialIntervalMs();
    },
    setEngaged(isEngaged: boolean): void {
      if (isEngaged && !isActive) {
        isActive = true;
        resetRepeatProgress();
        advanceUpgradeRepeatCadence(cadenceState, cadenceConfig, 0, {
          maxFires: 1,
        });
        let result: UpgradeHoverRepeatResult = undefined;
        beginBurst();
        try {
          result = options.onFire({
            nextIntervalMs: toEffectiveIntervalMs(
              cadenceState.currentBaseIntervalMs,
            ),
          });
        } finally {
          endBurst();
        }
        if (!isActive) {
          return;
        }
        if (result === false) {
          resetRepeatProgress();
          cadenceState.hasFiredInitial = true;
        }
        lastTickAtMs = Date.now();
        scheduleNext();
      } else if (!isEngaged && isActive) {
        stop();
      }
    },
    dispose(): void {
      stop();
    },
  };
}
